<template>
  <div class="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-2">
    <BlogMeta 
      :title="t('login.meta.title')"
      :description="t('login.meta.description')"
      :noIndex="true"
    />

    <!-- Contenedor principal del login -->
    <div class="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-1 sm:p-5 overflow-hidden">
      <div class="py-8 px-4 sm:px-10">
        <!-- Título del login -->
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-gray-900 text-center">
            {{ t('login.title') }}
          </h1>
        </div>

        <!-- Google Sign-In -->
        <div class="mb-6">
          <button
            type="button"
            @click="loginWithGoogle"
            :disabled="isLoading"
            class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{{ t('login.continueWithGoogle') }}</span>
          </button>
          
          <!-- Separador -->
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">{{ t('login.orContinueWith') }}</span>
            </div>
          </div>
        </div>
        
        <form class="space-y-6" @submit.prevent="login">
          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('login.emailLabel') }}
            </label>
            <input v-model="email" id="email" name="email" type="email" autocomplete="email" required
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
            >
          </div>

          <div class="mb-4">
            <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('login.passwordLabel') }}
            </label>
            <div class="relative">
              <input 
                v-model="password" 
                id="password" 
                name="password" 
                :type="showPassword ? 'text' : 'password'" 
                autocomplete="current-password" 
                required
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              >
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                <!-- Ojo abierto (mostrar contraseña) -->
                <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <!-- Ojo cerrado (ocultar contraseña) -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Error message -->
          <div v-if="errorMessage" class="mb-4 rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">{{ errorMessage }}</h3>
              </div>
            </div>
          </div>

          <div class="mb-6">
            <button type="submit"
              class="w-full inline-flex items-center justify-center py-2 px-4 border border-indigo-200 rounded-full shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              :disabled="isLoading"
            >
              <LoadingSpinner v-if="isLoading" size="small" color="indigo" class="mr-2" />
              <span v-if="isLoading">{{ t('login.loggingInButton') }}</span>
              <span v-else>{{ t('login.loginButton') }}</span>
            </button>
          </div>
        </form>

        <!-- Development login helper -->
        <div v-if="isDevelopment" class="mt-6 p-3 bg-yellow-50 rounded-md">
          <h3 class="text-sm font-medium text-yellow-800">{{ t('login.devHelper.title') }}</h3>
          <p class="mt-1 text-sm text-yellow-700">
            {{ t('login.devHelper.info') }}
          </p>
          <div class="mt-2 text-sm space-y-4">
            <!-- Owner credentials -->
            <div class="p-2 bg-white rounded-md">
              <h4 class="font-medium text-gray-800 mb-2">{{ t('login.devHelper.ownerTitle') }}</h4>
              <div class="flex justify-between">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.emailLabel') }}</span>
                <span class="text-gray-600 select-all">testowner@notarial.online</span>
              </div>
              <div class="flex justify-between mt-1">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.passwordLabel') }}</span>
                <span class="text-gray-600 select-all">password123</span>
              </div>
              <button 
                @click="fillTestCredentials('owner')" 
                class="mt-2 w-full bg-yellow-100 py-1 px-2 rounded-full text-yellow-800 text-sm hover:bg-yellow-200 transition-colors duration-200"
              >
                {{ t('login.devHelper.fillOwnerButton') }}
              </button>
            </div>

            <!-- Applicant credentials -->
            <div class="p-2 bg-white rounded-md">
              <h4 class="font-medium text-gray-800 mb-2">{{ t('login.devHelper.applicantTitle') }}</h4>
              <div class="flex justify-between">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.emailLabel') }}</span>
                <span class="text-gray-600 select-all">testapplicant@notarial.online</span>
              </div>
              <div class="flex justify-between mt-1">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.passwordLabel') }}</span>
                <span class="text-gray-600 select-all">password123</span>
              </div>
              <button 
                @click="fillTestCredentials('applicant')" 
                class="mt-2 w-full bg-yellow-100 py-1 px-2 rounded-full text-yellow-800 text-sm hover:bg-yellow-200 transition-colors duration-200"
              >
                {{ t('login.devHelper.fillApplicantButton') }}
              </button>
            </div>
            
            <!-- Agent credentials -->
            <div class="p-2 bg-white rounded-md">
              <h4 class="font-medium text-gray-800 mb-2">{{ t('login.devHelper.agentTitle', 'Agente') }}</h4>
              <div class="flex justify-between">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.emailLabel') }}</span>
                <span class="text-gray-600 select-all">testagent@notarial.online</span>
              </div>
              <div class="flex justify-between mt-1">
                <span class="font-medium text-gray-700">{{ t('login.devHelper.passwordLabel') }}</span>
                <span class="text-gray-600 select-all">password123</span>
              </div>
              <button 
                @click="fillTestCredentials('agent')" 
                class="mt-2 w-full bg-yellow-100 py-1 px-2 rounded-full text-yellow-800 text-sm hover:bg-yellow-200 transition-colors duration-200"
              >
                {{ t('login.devHelper.fillAgentButton', 'Rellenar como Agente') }}
              </button>
            </div>
          </div>
        </div>

        <p class="mt-6 text-center text-sm text-gray-600">
          {{ t('login.signupQuestion') }}
          <router-link to="/signup" class="font-medium text-indigo-600 hover:text-indigo-500">
            {{ t('login.signupLink') }}
          </router-link>
        </p>
        <p class="mt-2 text-center text-sm text-gray-600">
          {{ t('login.forgotPasswordQuestion') }}
          <router-link to="/forgot-password" class="font-medium text-indigo-600 hover:text-indigo-500">
            {{ t('login.forgotPasswordLink') }}
          </router-link>
        </p>
      </div>
    </div>
    
    <ToastComponent v-if="toastMessage" :message="toastMessage" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { signInWithEmailAndPassword, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth'
import { auth } from '../firebase'
import { useRouter, useRoute } from 'vue-router'
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../firebase'
import BlogMeta from './BlogMeta.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ToastComponent from './ToastComponent.vue'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/composables/useAnalytics'
import { getRecaptchaTokenForApi, getRecaptchaErrorMessage } from '@/utils/recaptcha'

export default {
  name: 'LoginPage',
  components: {
    BlogMeta,
    LoadingSpinner,
    ToastComponent
  },
  setup() {
    const { t } = useI18n()
    const { trackLoginError } = useAnalytics()
    const email = ref('')
    const password = ref('')
    const showPassword = ref(false)
    const errorMessage = ref('')
    const isLoading = ref(false)
    const router = useRouter()
    const route = useRoute()
    const toastMessage = ref('')

    // Detectar si estamos en modo desarrollo
    const isDevelopment = computed(() => {
      return process.env.NODE_ENV === 'development' || process.env.VUE_APP_ENV === 'development'
    })

    const fillTestCredentials = (userType) => {
      password.value = 'password123'
      if (userType === 'owner') {
        email.value = 'testowner@notarial.online'
      } else if (userType === 'agent') {
        email.value = 'testagent@notarial.online'
      } else {
        email.value = 'testapplicant@notarial.online'
      }
    }

    const loginWithGoogle = async () => {
      if (isLoading.value) return
      
      errorMessage.value = ''
      isLoading.value = true

      try {
        const provider = new GoogleAuthProvider()
        provider.setCustomParameters({
          prompt: 'select_account'
        })

        // Abrir popup de Google
        const result = await signInWithPopup(auth, provider)
        const user = result.user

        console.log('[Login Google] Usuario autenticado:', user.uid)

        // Verificar si el usuario existe en Firestore
        const userDocRef = doc(db, 'users', user.uid)
        const userDoc = await getDoc(userDocRef)

        if (!userDoc.exists()) {
          // Usuario nuevo con Google - crear como applicant
          const userData = {
            email: user.email,
            fullName: user.displayName || '',
            userType: 'applicant',
            profileComplete: false,
            createdAt: new Date(),
            authProvider: 'google',
            emailVerified: true,
            emailVerifiedAt: new Date()
          }

          console.log('[Login Google] Creando documento en Firestore para nuevo usuario applicant')
          await setDoc(userDocRef, userData)

          // Completar registro Google en el backend (envía welcome email server-side)
          try {
            const recaptchaToken = await getRecaptchaTokenForApi('google_signup')
            const registrationResponse = await fetch('/api/email/complete-google-registration', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: user.uid,
                userEmail: user.email,
                userName: user.displayName || user.email?.split('@')[0] || 'Usuario',
                ...(recaptchaToken && { recaptchaToken }),
              })
            })
            const registrationResult = await registrationResponse.json()
            if (!registrationResult.success) {
              console.warn('[Login Google] Registro backend no completado:', registrationResult.message)
            }
          } catch (registrationError) {
            console.error('[Login Google] Error completando registro en backend (no crítico):', registrationError)
          }
        } else if (await blockDisabledAccount(userDoc.data())) {
          return
        }

        // Mostrar mensaje de éxito
        showToast(t('login.success'))
        
        // Redirigir según el tipo de usuario
        await new Promise(resolve => setTimeout(resolve, 100))
        await handleRedirect()

      } catch (error) {
        console.error('[Login Google] Error:', error)
        
        // Track login error
        trackLoginError({
          method: 'google',
          errorCode: error.code || 'unknown',
          errorMessage: error.message || ''
        })
        
        if (error.code === 'auth/popup-closed-by-user') {
          errorMessage.value = 'Has cerrado la ventana de Google'
        } else if (error.code === 'auth/cancelled-popup-request') {
          errorMessage.value = 'Solicitud cancelada'
        } else if (error.code === 'auth/popup-blocked') {
          errorMessage.value = 'El popup fue bloqueado. Por favor, permite popups en tu navegador.'
        } else {
          errorMessage.value = 'Error al iniciar sesión con Google. Intenta de nuevo.'
        }
      } finally {
        isLoading.value = false
      }
    }

    const login = async () => {
      errorMessage.value = ''
      isLoading.value = true
      
      try {
        // Modo de simulación para desarrollo
        if (process.env.VUE_APP_ENABLE_SIGNUP_SIMULATION === 'true') {
          let simulatedUser = null

          // Verificar credenciales de prueba para owner
          if (email.value === 'testowner@notarial.online' && password.value === 'password123') {
            simulatedUser = {
              uid: 'dev-owner-123',
              email: 'testowner@notarial.online',
              displayName: 'Usuario Propietario de Prueba',
              userType: 'owner'
            }
          }
          // Verificar credenciales de prueba para applicant
          else if (email.value === 'testapplicant@notarial.online' && password.value === 'password123') {
            simulatedUser = {
              uid: 'dev-applicant-123',
              email: 'testapplicant@notarial.online',
              displayName: 'Usuario Solicitante de Prueba',
              userType: 'applicant'
            }
          }
          // Verificar credenciales de prueba para agent
          else if (email.value === 'testagent@notarial.online' && password.value === 'password123') {
            simulatedUser = {
              uid: 'dev-agent-123',
              email: 'testagent@notarial.online',
              displayName: 'Usuario Agente de Prueba',
              userType: 'agent',
              agencyName: 'Agencia Inmobiliaria de Prueba',
              professionalLicense: 'AG-12345',
              managedOwners: []
            }
          }

          if (simulatedUser) {
            // Guardar usuario en sessionStorage para desarrollo
            sessionStorage.setItem('dev_auth_user', JSON.stringify(simulatedUser))
            // ✅ Mostrar mensaje de éxito
            showToast(t('login.success'))
            handleRedirect()
            return
          }
          
          // Si las credenciales no coinciden
          errorMessage.value = t('login.errorMessages.devCredentialsIncorrect')
          return
        }
        
        // Intento normal de inicio de sesión con Firebase
        const userCredential = await signInWithEmailAndPassword(auth, email.value, password.value)
        const user = userCredential.user
        
        // REGISTRO PROGRESIVO: Ya no bloqueamos por email no verificado
        // El email no verificado no impide usar la app, solo algunas funciones avanzadas
        const userDocRef = doc(db, 'users', user.uid)
        const userDoc = await getDoc(userDocRef)
        
        if (userDoc.exists()) {
          const userData = userDoc.data()

          if (await blockDisabledAccount(userData)) {
            return
          }
          
          // Mostrar recordatorio amigable si el email no está verificado (no bloquea)
          if (!userData.emailVerified) {
            // No bloqueamos - el usuario puede usar la app
          }
        }
        
        // ✅ Mostrar mensaje de éxito antes de redirigir
        showToast(t('login.success'))
        
        handleRedirect()
      } catch (error) {
        console.error('Login error:', error)
        
        // Track login error
        trackLoginError({
          method: 'email',
          errorCode: error.code || 'unknown',
          errorMessage: error.message || ''
        })
        
        // Mostrar mensaje de error apropiado usando traducciones
        switch(error.code) {
          case 'auth/invalid-credential':
            errorMessage.value = t('login.errorMessages.invalidCredentials')
            break
          case 'auth/user-not-found':
            errorMessage.value = t('login.errorMessages.userNotFound')
            break
          case 'auth/wrong-password':
            errorMessage.value = t('login.errorMessages.wrongPassword')
            break
          case 'auth/invalid-email':
            errorMessage.value = t('login.errorMessages.invalidEmail')
            break
          case 'auth/too-many-requests':
            errorMessage.value = t('login.errorMessages.tooManyRequests')
            break
          default:
            errorMessage.value = t('login.errorMessages.generic')
        }
      } finally {
        isLoading.value = false
      }
    }

    const sanitizeInternalRedirect = (path) => {
      if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
        return null
      }
      return path
    }

    const redirectDevSimulatedUser = (simulatedUser) => {
      const userType = simulatedUser?.userType
      if (userType === 'agent') {
        router.push('/agent-dashboard')
      } else if (userType === 'admin') {
        router.push('/admin')
      } else if (userType === 'managedOwner') {
        router.push('/owner-dashboard')
      } else {
        router.push('/contracts')
      }
    }

    const handleRedirect = async () => {
      const redirectPath = sanitizeInternalRedirect(route.query.redirect)
      const savedRedirect = sanitizeInternalRedirect(sessionStorage.getItem('redirectAfterLogin'))
      
      // ✅ Esperar 100ms para que la navbar se actualice antes de redirigir
      await new Promise(resolve => setTimeout(resolve, 100))
      
      if (redirectPath) {
        router.push(redirectPath)
        return
      } 
      
      if (savedRedirect) {
        sessionStorage.removeItem('redirectAfterLogin')
        router.push(savedRedirect)
        return
      }

      // Modo dev: auth simulada sin Firebase currentUser
      if (process.env.VUE_APP_ENABLE_SIGNUP_SIMULATION === 'true') {
        const devUser = sessionStorage.getItem('dev_auth_user')
        if (devUser) {
          redirectDevSimulatedUser(JSON.parse(devUser))
          return
        }
      }
      
      // Si no hay redirect específico, detectar tipo de usuario para redirigir apropiadamente
      try {
        // Esperar a que Firebase Auth se inicialice completamente
        if (auth.currentUser) {
          // Obtener el tipo de usuario desde Firestore usando doc directo (más eficiente)
          const userDocRef = doc(db, 'users', auth.currentUser.uid)
          const userDoc = await getDoc(userDocRef)
          
          if (userDoc.exists()) {
            const userData = userDoc.data()
            if (await blockDisabledAccount(userData)) {
              return
            }
            const userType = userData.userType
            
            // Redirigir según el tipo de usuario
            if (userType === 'agent') {
              router.push('/agent-dashboard')
            } else if (userType === 'admin') {
              router.push('/admin')
            } else if (userType === 'managedOwner') {
              router.push('/owner-dashboard')
            } else {
              // Para applicants: si no tienen ninguna solicitud activa, enviar al buscador
              try {
                const appsQuery = query(
                  collection(db, 'colivingApplications'),
                  where('applicantId', '==', auth.currentUser.uid),
                  where('status', 'in', ['submitted', 'under_review', 'pre_approved', 'contract_signed', 'room_assigned'])
                )
                const appsSnapshot = await getDocs(appsQuery)
                if (appsSnapshot.empty) {
                  router.push('/encuentratucoliving')
                  return
                }
              } catch {
                // Si falla la consulta, ir a /contracts como fallback
              }
              router.push('/contracts')
            }
            return
          }
        }
      } catch (error) {
        console.error('Error detectando tipo de usuario para redirección:', error)
      }
      
      // Fallback: ir a contracts si algo falla
      router.push('/contracts')
    }

    const showToast = (message) => {
      toastMessage.value = message
      setTimeout(() => {
        toastMessage.value = ''
      }, 3000)
    }

    const blockDisabledAccount = async (userData) => {
      if (!userData || !['suspended', 'deleted'].includes(userData.accountStatus)) {
        return false
      }

      await signOut(auth)
      errorMessage.value = userData.accountStatus === 'deleted'
        ? 'Esta cuenta ha sido eliminada.'
        : 'Esta cuenta está suspendida. Contacta con soporte.'
      return true
    }

    // Función para obtener la ruta de redirección según el tipo de usuario
    const getRedirectByUserType = (userType) => {
      switch (userType) {
        case 'admin':
          return '/admin'  // Redirige directamente a /admin
        case 'agent':
          return '/agent-dashboard'
        case 'applicant':
          return '/contracts'
        case 'managedOwner':
          return '/owner-dashboard'
        case 'owner':
        default:
          return '/contracts'
      }
    }

    // Verificar si el usuario ya está autenticado al montar el componente
    let authUnsubscribe = null

    onMounted(() => {
      if (auth.currentUser) {
        redirectAuthenticatedUser()
        return
      }
      
      authUnsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          await redirectAuthenticatedUser()
        }
      })
    })

    onUnmounted(() => {
      if (authUnsubscribe) {
        authUnsubscribe()
        authUnsubscribe = null
      }
    })

    // Función para redirigir usuarios ya autenticados
    const redirectAuthenticatedUser = async () => {
      try {
        const user = auth.currentUser
        if (!user) return

        const userDocRef = doc(db, 'users', user.uid)
        const userDoc = await getDoc(userDocRef)
        
        if (userDoc.exists()) {
          const userData = userDoc.data()

          if (await blockDisabledAccount(userData)) {
            return
          }
          
          if (!userData.emailVerified) {
            return
          }
          
          const userType = userData.userType

          if (userType === 'applicant') {
            // Para applicants sin solicitudes activas → buscador
            try {
              const appsQuery = query(
                collection(db, 'colivingApplications'),
                where('applicantId', '==', user.uid),
                where('status', 'in', ['submitted', 'under_review', 'pre_approved', 'contract_signed', 'room_assigned'])
              )
              const appsSnapshot = await getDocs(appsQuery)
              router.push(appsSnapshot.empty ? '/encuentratucoliving' : '/contracts')
            } catch {
              router.push('/contracts')
            }
            return
          }

          router.push(getRedirectByUserType(userType))
        }
      } catch (error) {
        console.error('[Login] Error al verificar usuario autenticado:', error)
      }
    }

    return { 
      t,
      email, 
      password,
      showPassword,
      login,
      loginWithGoogle, // ⬅️ AÑADIDO: Función Google Sign-In
      errorMessage,
      isLoading,
      isDevelopment,
      fillTestCredentials,
      toastMessage,
      showToast
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>